import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import DropdownMenu from '../components/ui/DropdownMenu';
import InputField from '../components/forms/form-elements/InputField';
import Button from '../components/buttons/Button';
import { MenuItem, Complex, Field, SelectOption } from '../types';
import SwitchButton from '../components/buttons/SwitchButton';
import MyDrawer from '../components/ui/Drawer';
import DateGridCalendar from '../components/calendar/date-calendar';
import TimeGridCalendar from '../components/calendar/time-calendar';
import dayjs from 'dayjs';
import { getToken } from '../utils/token';
import Divider from '../components/ui/Divider';
import { toast } from 'react-toastify';
import { useAvailableReservations } from '../hooks/useAvailableReservations';
import { useTranslation } from 'react-i18next';
import { COMMON_TRANSLATE_KEYS } from '../constants/translate-keys';
import * as COMMON_ERROR_KEYS from '../constants/errors';
import { useResponsiveWindow } from '../hooks/useResponsiveWindow';
import { CreateReservationInput } from '../types/reservations';
import LoadingBall from '../components/loading/LoadingBall';
import { ROUTE_MY_FIELDS } from '../router/constants';
import GroupsSelect from '../components/select-with-search/GroupsSelect';
import GroupBar from '../components/ui/GroupBar';
import { PHONE_REGEX } from '../utils/regex';

interface IFormInput {
  otherPersonName?: string;
  otherPersonPhone?: string;
  time: string;
  date: string;
  numberOfWeeks: string;
  startRecurringDate: string;
}

const BookField: React.FC = () => {
  const { complexId, fieldId } = useParams<{
    complexId: string;
    fieldId: string;
  }>();

  const [query] = useSearchParams();

  const { isMobile } = useResponsiveWindow();

  const navigate = useNavigate();

  const { t, i18n } = useTranslation();

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    unregister,
    reset: resetFormValues,
    formState: { errors, isValid, isSubmitting },
  } = useForm<IFormInput>({
    defaultValues: {
      otherPersonName: '',
      otherPersonPhone: '',
      time: '',
      date: '',
      numberOfWeeks: '',
      startRecurringDate: '',
    },
    mode: 'onChange',
  });

  const [loadingComplexes, setLoadingComplexes] = useState(true);
  const [loadingFields, setLoadingFields] = useState(true);
  const [complexes, setComplexes] = useState<Array<Complex>>([]);
  const [fields, setFields] = useState<Array<Field>>([]);
  const [selectedComplex, setSelectedComplex] =
    useState<MenuItem<Complex> | null>(null);
  const [selectedField, setSelectedField] = useState<MenuItem<Field> | null>(
    null
  );
  const [isForAnotherPerson, setIsForAnotherPerson] = useState<boolean>(false);
  const [isRecurring, setIsRecurring] = useState<boolean>(false);
  const [days, setDays] = useState<Array<boolean>>([
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ]);
  const [isDateDrawerOpen, setIsDateDrawerOpen] = useState(false);
  const [isTimeDrawerOpen, setIsTimeDrawerOpen] = useState(false);
  const [isRecurringDateDrawerOpen, setIsRecurringDateDrawerOpen] =
    useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedRecurringDate, setSelectedRecurringDate] =
    useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [fieldImages, setFieldImages] = useState<Array<string>>([]);
  const [groupName, setGroupName] = useState<string>('');
  const [selectedGroup, setSelectedGroup] = useState<SelectOption | null>(null);

  const [showGroup, setShowGroup] = useState(false);

  const {
    fetch: fetchAvailableReservations,
    getDates,
    getTimeSlotsForDate,
  } = useAvailableReservations();

  const queryDate = useMemo(() => {
    const date = query.get('d');
    return date && dayjs(date).isValid() ? date : null;
  }, [query]);

  const startTime = useMemo(() => {
    const time = query.get('s');
    return time && dayjs(time, 'HH:mm').isValid()
      ? dayjs(time, 'HH:mm').format('HH:mm')
      : null;
  }, [query]);

  const endTime = useMemo(() => {
    const time = query.get('e');
    return time && dayjs(time, 'HH:mm').isValid()
      ? dayjs(time, 'HH:mm').format('HH:mm')
      : null;
  }, [query]);

  const fetchReservationSlots = useCallback(
    async (fieldIdArg?: number, monthArg?: number, yearArg?: number) => {
      let selectedFieldId;

      if (fieldIdArg) {
        selectedFieldId = fieldIdArg;
      } else if (selectedField) {
        selectedFieldId = selectedField.value.id;
      } else {
        return;
      }

      let month = monthArg;
      let year = yearArg;

      if (!month || !year) {
        month = dayjs().month();
        year = dayjs().year();
      }

      await fetchAvailableReservations(selectedFieldId, month, year, true);
    },
    [fetchAvailableReservations, selectedField]
  );

  const closeDrawer = () => {
    setIsDateDrawerOpen(false);
    setIsTimeDrawerOpen(false);
  };

  const closeRecurringDateDrawer = () => {
    setIsRecurringDateDrawerOpen(false);
    setIsTimeDrawerOpen(false);
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    setValue('time', time, { shouldValidate: true });
    closeDrawer();
  };

  const resetTime = () => {
    setSelectedTime(null);
    setValue('time', '');
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setValue('date', dayjs(date).format('YYYY-MM-DD'), {
      shouldValidate: true,
    });
    resetTime();
    closeDrawer();
  };

  const handleRecurringDateSelect = (date: Date) => {
    setSelectedRecurringDate(date);
    setValue('startRecurringDate', dayjs(date).format('YYYY-MM-DD'), {
      shouldValidate: true,
    });
    closeRecurringDateDrawer();
  };

  const handleIsRecurringClick = () => {
    setIsRecurring(!isRecurring);
    resetTime();
  };

  const fetchComplexes = async (page: number = 1) => {
    try {
      setLoadingComplexes(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/owner/complexes?page=${page}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      setComplexes(response.data);
    } catch (error) {
      console.error('Error fetching complexes:', error);
    } finally {
      setLoadingComplexes(false);
    }
  };

  const fetchFields = async (
    complexId: number,
    page: number = 1,
    limit: number = 50
  ) => {
    try {
      setLoadingFields(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/owner/complexes/${complexId}/fields?page=${page}&limit=${limit}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );

      setFields(response.data.fields);
    } catch (error) {
      console.error('Error fetching fields:', error);
    } finally {
      setLoadingFields(false);
    }
  };

  const handleComplexClick = (option: MenuItem<Complex>) => {
    setSelectedComplex(option);
  };

  const handleFieldClick = useCallback(
    async (option: MenuItem<Field>) => {
      setSelectedField(option);
      setFieldImages(option.value.imageUrls || []);

      if (option.value.isUnavailable) {
        toast(t(COMMON_TRANSLATE_KEYS.FIELD_IS_UNAVAILABLE), {
          toastId: 'field-unavailable',
          theme: 'dark',
          className: 'text-center',
          bodyClassName: 'custom-toast-body',
        });
        return;
      }

      const month = selectedDate?.getMonth() || undefined;
      const year = selectedDate?.getFullYear() || undefined;

      await fetchReservationSlots(option.value.id, month, year);
    },
    [fetchReservationSlots, selectedDate, t]
  );

  const onSubmit = async (data: IFormInput) => {
    if (!selectedField) {
      toast.error(t(COMMON_TRANSLATE_KEYS.SELECT_FIELD_ERROR));
      return;
    }

    if (!isRecurring && !selectedDate) {
      toast.error(t(COMMON_TRANSLATE_KEYS.SELECT_DATE_ERROR));
      return;
    }

    if (!isRecurring && !data.date) {
      setError('date', { message: t(COMMON_TRANSLATE_KEYS.SELECT_DATE_ERROR) });
      setIsDateDrawerOpen(true);
      return;
    }

    if (!data.time) {
      setError('time', { message: t(COMMON_TRANSLATE_KEYS.SELECT_TIME_ERROR) });
      setIsTimeDrawerOpen(true);
      return;
    }

    if (isRecurring && days.filter((x) => x).length === 0) {
      toast.error(t(COMMON_TRANSLATE_KEYS.SELECT_DAYS_ERROR));
      return;
    }

    if (isRecurring && !data.numberOfWeeks) {
      toast.error(t(COMMON_ERROR_KEYS.NUMBER_OF_WEEKS_ERROR));
      return;
    }

    const [start, end] = data.time.split('-');

    let formData: CreateReservationInput = {
      fieldId: selectedField.value.id,
      startTime: start,
      endTime: end,
      isForAnotherPerson,
      name: isForAnotherPerson ? data.otherPersonName : undefined,
      phone: isForAnotherPerson ? data.otherPersonPhone : undefined,
      isRecurringEvent: isRecurring,
      teamId: selectedGroup?.value?.id,
    };

    if (isRecurring) {
      formData = {
        ...formData,
        startRecurringDate: data.startRecurringDate,
        weeks: Number(data.numberOfWeeks),
        days: days.reduce<number[]>((curr, next, i) => {
          if (next) {
            curr.push(i);
          }
          return curr;
        }, []),
      };
    } else {
      formData.date = data.date;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}/owner/reservations`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'application/json',
            'Accept-Language': i18n.language,
          },
        }
      );

      toast.success(t(COMMON_TRANSLATE_KEYS.RESERVATION_CREATED), {
        theme: 'dark',
        className: 'text-center',
        bodyClassName: 'custom-toast-body',
      });

      if (selectedDate) {
        await fetchAvailableReservations(
          formData.fieldId,
          selectedDate.getMonth(),
          selectedDate.getFullYear(),
          true
        );
      } else {
        await fetchAvailableReservations(
          formData.fieldId,
          dayjs().month(),
          dayjs().year(),
          true
        );
      }

      resetForm();
      navigate(`/arena/${complexId}/field-reservation/${fieldId}`);
    } catch (error: any) {
      // if (
      //   error?.response?.data?.error?.startsWith(
      //     'Reservation must be made between the following hours:'
      //   )
      // ) {
      //   setError('time', { message: error?.response?.data?.error });
      //   return;
      // }

      // if (error?.response?.data?.error?.startsWith('Date has')) {
      //   setError('date', { message: error?.response?.data?.error });
      //   return;
      // }

      if (error?.response?.data?.error) {
        toast.error(error?.response?.data?.error, {
          theme: 'dark',
          className: 'text-center',
          bodyClassName: 'custom-toast-body',
        });
        return;
      }

      toast.error(t(COMMON_TRANSLATE_KEYS.UNEXPECTED_ERROR), {
        theme: 'dark',
        className: 'text-center',
        bodyClassName: 'custom-toast-body',
      });
    }
  };

  const resetForm = () => {
    resetFormValues();
    setSelectedComplex(null);
    setSelectedField(null);
    setIsForAnotherPerson(false);
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedRecurringDate(null);
    setFieldImages([]);
  };

  const handleCancel = () => {
    resetForm();
    navigate(`/arena/${complexId}/field-reservation/${fieldId}`);
  };

  const complexOptions: Array<MenuItem<Complex>> = complexes.map(
    (complex: Complex) => {
      const option = {
        label: complex.name,
        value: complex,
        onClick: () => {},
      };

      option.onClick = () => handleComplexClick(option);

      return option;
    }
  );

  const fieldOptions: Array<MenuItem<Field>> = fields.map((field: Field) => {
    const option: MenuItem<Field> = {
      label: field.name,
      value: field,
      onClick: () => {},
    };

    option.onClick = () => handleFieldClick(option);
    return option;
  });

  const filteredDates = useMemo(() => {
    if (selectedField?.value?.isUnavailable) {
      return [];
    }

    return getDates(selectedField?.value?.id);
  }, [selectedField, getDates]);

  const timeSlots = useMemo(() => {
    const slots = getTimeSlotsForDate(
      selectedField?.value?.id,
      selectedDate || undefined
    );

    return slots;
  }, [selectedField, selectedDate, getTimeSlotsForDate]);

  useEffect(() => {
    fetchComplexes();
  }, []);

  useEffect(() => {
    if (
      complexId &&
      complexId.length > 0 &&
      !selectedComplex &&
      complexOptions.length > 0
    ) {
      const complexOption = complexOptions.find((option) => {
        return option.value.id.toString() === complexId;
      });
      if (complexOption) {
        setSelectedComplex(complexOption);
      } else {
        navigate(ROUTE_MY_FIELDS, { replace: true });
      }
    }
  }, [complexOptions, complexId, selectedComplex, navigate]);

  useEffect(() => {
    if (selectedComplex) fetchFields(selectedComplex.value.id);
  }, [selectedComplex]);

  useEffect(() => {
    if (
      fieldId &&
      fieldId.length > 0 &&
      !selectedField &&
      fieldOptions.length > 0
    ) {
      const fieldOption = fieldOptions.find((option) => {
        return option.value.id.toString() === fieldId;
      });
      if (fieldOption) {
        setSelectedField(fieldOption);

        const { imageUrls, id, isUnavailable } = fieldOption.value;

        setFieldImages(imageUrls || []);
        fetchReservationSlots(id);

        if (isUnavailable) {
          toast(t(COMMON_TRANSLATE_KEYS.FIELD_IS_UNAVAILABLE), {
            theme: 'dark',
            className: 'text-center',
            bodyClassName: 'custom-toast-body',
          });
        }
      } else {
        navigate(ROUTE_MY_FIELDS, { replace: true });
      }
    }
  }, [
    fieldOptions,
    fieldId,
    selectedField,
    fetchReservationSlots,
    t,
    navigate,
  ]);

  useEffect(() => {
    if (queryDate && startTime && endTime) {
      setSelectedDate(dayjs(queryDate, 'DD-MM-YYYY').toDate());
      setValue('date', queryDate, { shouldValidate: true });
      setSelectedTime(`${startTime}-${endTime}`);
      setValue('time', `${startTime}-${endTime}`, { shouldValidate: true });
    }
  }, [queryDate, startTime, endTime, setValue]);

  if (loadingComplexes || loadingFields) {
    return <LoadingBall className="w-full h-screen" />;
  }

  return (
    <div className="flex flex-col md:mx-auto mx-10 mb-4 mt-8">
      <div className="w-full h-64 mb-4">
        <img
          src={fieldImages.length > 0 ? fieldImages[0] : '/arena-arda.png'}
          alt={t(COMMON_TRANSLATE_KEYS.FIELD)}
          className="flex object-cover w-full h-full rounded-2xl overflow-hidden"
          style={{ width: '608px', height: '256px' }}
        />
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
        <div className="mt-4 flex flex-col">
          <p className="text-mainWhite mt-4 px-2 py-3 bg-backGroundGray rounded-2xl">
            {`${t(COMMON_TRANSLATE_KEYS.COMPLEX)}: ${selectedComplex?.label}`}
          </p>
          {/* <div className="flex flex-row justify-between">
            <DropdownMenu<Complex>
              items={complexOptions}
              selectedOption={selectedComplex}
              setSelectedOption={handleComplexClick}
              showIcon={false}
              width={38}
            />
          </div> */}
        </div>

        <div>
          <p className="text-mainWhite px-2 py-3 bg-backGroundGray rounded-2xl">
            {`${t(COMMON_TRANSLATE_KEYS.FIELD)}: ${selectedField?.label}`}
          </p>
          {/* <div className="flex flex-row justify-between">
            <DropdownMenu<Field>
              items={fieldOptions}
              selectedOption={selectedField}
              setSelectedOption={handleFieldClick}
              showIcon={false}
              width={38}
            />
          </div> */}
        </div>

        <div className="flex flex-row justify-between">
          <SwitchButton
            width={38}
            isSelected={isForAnotherPerson}
            onClick={() => {
              const newValue = !isForAnotherPerson;
              setIsForAnotherPerson(newValue);
              if (!newValue) {
                unregister('otherPersonName');
                unregister('otherPersonPhone');
              }
            }}
          >
            {t(COMMON_TRANSLATE_KEYS.RESERVE_FOR_ANOTHER_PERSON)}
          </SwitchButton>
        </div>

        {isForAnotherPerson && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col">
              <p className="text-mainWhite">
                {t(COMMON_TRANSLATE_KEYS.NAME_OF_THE_PERSON)}{' '}
                <span className="text-functionalRed">*</span>
              </p>
              <div className="flex flex-col justify-between">
                <InputField
                  placeholder=""
                  width={38}
                  {...register('otherPersonName', {
                    required: {
                      value: isForAnotherPerson,
                      message: t(COMMON_ERROR_KEYS.NAME_IS_REQUIRED),
                    },
                  })}
                  error={errors.otherPersonName?.message}
                />
              </div>
            </div>
            <div className="flex flex-col">
              <p className="text-mainWhite">
                {t(COMMON_TRANSLATE_KEYS.PHONE_OF_THE_PERSON)}
                <span className="text-functionalRed">*</span>
              </p>
              <div className="flex flex-col justify-between">
                <InputField
                  placeholder=""
                  width={38}
                  {...register('otherPersonPhone', {
                    required: {
                      value: isForAnotherPerson,
                      message: t(COMMON_ERROR_KEYS.PHONE_IS_REQUIRED),
                    },
                    pattern: {
                      value: PHONE_REGEX,
                      message: t(COMMON_ERROR_KEYS.PHONE_IS_INVALID),
                    },
                  })}
                  error={errors.otherPersonPhone?.message}
                />
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col">
          <p className="text-mainWhite mb-2">
            {t(COMMON_TRANSLATE_KEYS.TEAM)}{' '}
            <span className="text-textGray">{`(${t(
              COMMON_TRANSLATE_KEYS.OPTIONAL
            )})`}</span>
          </p>
          <div className="flex flex-col justify-between">
            <GroupsSelect
              value={groupName}
              onChange={setGroupName}
              onSelectOption={(o) => setSelectedGroup(o)}
              selectedOption={selectedGroup}
              placeholder={t(COMMON_TRANSLATE_KEYS.ENTER_GROUP_NAME_AND_SELECT)}
            />
          </div>
        </div>
        {selectedGroup && (
          <GroupBar
            openDrawer={showGroup}
            setOpenDrawer={setShowGroup}
            group={selectedGroup.value}
            onDiscardClick={(e) => {
              e.preventDefault();
              setSelectedGroup(null);
              setGroupName('');
            }}
          />
        )}

        <div className="flex flex-row justify-between">
          <SwitchButton
            width={38}
            isSelected={isRecurring}
            onClick={() => handleIsRecurringClick()}
          >
            {t(COMMON_TRANSLATE_KEYS.RECURRING_RESERVATION)}
          </SwitchButton>
        </div>

        {!isRecurring && (
          <div className="flex sm:flex-row flex-col justify-between gap-6">
            <div className="w-full">
              <p className="text-mainWhite mb-2">
                {t(COMMON_TRANSLATE_KEYS.DATE)}
                <span className="text-functionalRed">*</span>
              </p>
              <InputField
                placeholder={t(COMMON_TRANSLATE_KEYS.SELECT_DATE)}
                width={isMobile ? undefined : 18}
                {...register('date', {
                  required: {
                    value: true,
                    message: t(COMMON_TRANSLATE_KEYS.SELECT_DATE_ERROR),
                  },
                })}
                error={errors.date?.message}
                onClick={() => setIsDateDrawerOpen(true)}
                readOnly
                value={
                  selectedDate ? dayjs(selectedDate).format('DD.MM.YYYY') : ''
                }
                color="text-mainBlue"
              />
            </div>
            <div className="w-full">
              <p className="text-mainWhite mb-2">
                {t(COMMON_TRANSLATE_KEYS.TIME)}
                <span className="text-functionalRed">*</span>
              </p>
              <InputField
                placeholder={t(COMMON_TRANSLATE_KEYS.SELECT_TIME)}
                width={isMobile ? undefined : 18}
                {...register('time', {
                  required: {
                    value: true,
                    message: t(COMMON_TRANSLATE_KEYS.SELECT_TIME_ERROR),
                  },
                })}
                error={errors.time?.message}
                onClick={() => setIsTimeDrawerOpen(true)}
                readOnly
                value={selectedTime || ''}
                disabled={!selectedDate}
              />
            </div>
          </div>
        )}

        {isRecurring && (
          <div className="flex flex-col gap-3">
            <div className="w-full">
              <p className="text-mainWhite mb-2">
                {t(COMMON_TRANSLATE_KEYS.START_DATE)}
                <span className="text-functionalRed">*</span>
              </p>
              <InputField
                placeholder={t(COMMON_TRANSLATE_KEYS.SELECT_DATE)}
                {...register('startRecurringDate', {
                  required: {
                    value: true,
                    message: t(COMMON_TRANSLATE_KEYS.SELECT_DATE_ERROR),
                  },
                })}
                error={errors.startRecurringDate?.message}
                onClick={() => setIsRecurringDateDrawerOpen(true)}
                readOnly
                value={
                  selectedRecurringDate
                    ? dayjs(selectedRecurringDate).format('DD.MM.YYYY')
                    : ''
                }
                color="text-mainBlue"
              />
            </div>
            <div className="flex sm:flex-row flex-col justify-between gap-6">
              <div className="w-full">
                <p className="text-mainWhite mb-2">
                  {t(COMMON_TRANSLATE_KEYS.FOR)}
                  <span className="text-functionalRed">*</span>
                </p>
                <InputField
                  placeholder={t(COMMON_TRANSLATE_KEYS.NUMBER_OF_WEEKS)}
                  width={isMobile ? undefined : 18}
                  {...register('numberOfWeeks', {
                    required: {
                      value: true,
                      message: t(COMMON_ERROR_KEYS.NUMBER_OF_WEEKS_ERROR),
                    },
                    min: {
                      value: 1,
                      message: t(COMMON_ERROR_KEYS.NUMBER_OF_WEEKS_ERROR),
                    },
                    max: {
                      value: 52,
                      message: t(COMMON_ERROR_KEYS.NUMBER_OF_WEEKS_ERROR),
                    },
                    validate: (value) => {
                      if (!Number(value)) {
                        return t(COMMON_ERROR_KEYS.NUMBER_OF_WEEKS_ERROR);
                      }
                    },
                  })}
                  error={errors.numberOfWeeks?.message}
                  color="text-mainBlue"
                />
              </div>
              <div className="w-full">
                <p className="text-mainWhite mb-2">
                  {t(COMMON_TRANSLATE_KEYS.TIME)}
                  <span className="text-functionalRed">*</span>
                </p>
                <InputField
                  placeholder={t(COMMON_TRANSLATE_KEYS.SELECT_TIME)}
                  width={isMobile ? undefined : 18}
                  {...register('time', {
                    required: {
                      value: true,
                      message: t(COMMON_TRANSLATE_KEYS.SELECT_TIME_ERROR),
                    },
                  })}
                  error={errors.time?.message}
                  onClick={() => setIsTimeDrawerOpen(true)}
                  readOnly
                  value={selectedTime || ''}
                  // disabled={!selectedDate}
                />
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <SwitchButton
                width={38}
                isSelected={days[1]}
                onClick={() =>
                  setDays((prev) => {
                    const newDays = [...prev];
                    newDays[1] = !newDays[1];
                    return newDays;
                  })
                }
              >
                {t(COMMON_TRANSLATE_KEYS.MONDAY_NORMAL)}
              </SwitchButton>
              <SwitchButton
                width={38}
                isSelected={days[2]}
                onClick={() =>
                  setDays((prev) => {
                    const newDays = [...prev];
                    newDays[2] = !newDays[2];
                    return newDays;
                  })
                }
              >
                {t(COMMON_TRANSLATE_KEYS.TUESDAY_NORMAL)}
              </SwitchButton>
              <SwitchButton
                width={38}
                isSelected={days[3]}
                onClick={() =>
                  setDays((prev) => {
                    const newDays = [...prev];
                    newDays[3] = !newDays[3];
                    return newDays;
                  })
                }
              >
                {t(COMMON_TRANSLATE_KEYS.WEDNESDAY_NORMAL)}
              </SwitchButton>
              <SwitchButton
                width={38}
                isSelected={days[4]}
                onClick={() =>
                  setDays((prev) => {
                    const newDays = [...prev];
                    newDays[4] = !newDays[4];
                    return newDays;
                  })
                }
              >
                {t(COMMON_TRANSLATE_KEYS.THURSDAY_NORMAL)}
              </SwitchButton>
              <SwitchButton
                width={38}
                isSelected={days[5]}
                onClick={() =>
                  setDays((prev) => {
                    const newDays = [...prev];
                    newDays[5] = !newDays[5];
                    return newDays;
                  })
                }
              >
                {t(COMMON_TRANSLATE_KEYS.FRIDAY_NORMAL)}
              </SwitchButton>
              <SwitchButton
                width={38}
                isSelected={days[6]}
                onClick={() =>
                  setDays((prev) => {
                    const newDays = [...prev];
                    newDays[6] = !newDays[6];
                    return newDays;
                  })
                }
              >
                {t(COMMON_TRANSLATE_KEYS.SATURDAY_NORMAL)}
              </SwitchButton>
              <SwitchButton
                width={38}
                isSelected={days[0]}
                onClick={() =>
                  setDays((prev) => {
                    const newDays = [...prev];
                    newDays[0] = !newDays[0];
                    return newDays;
                  })
                }
              >
                {t(COMMON_TRANSLATE_KEYS.SUNDAY_NORMAL)}
              </SwitchButton>
            </div>
          </div>
        )}

        <Divider width={'38'} />

        <div>
          <div className="flex flex-row justify-around gap-4">
            <Button
              textColor="text-mainWhite"
              backgroundColor="bg-functionalRed"
              width={18}
              onClick={handleCancel}
              height={3}
            >
              {t(COMMON_TRANSLATE_KEYS.CANCEL)}
            </Button>
            <Button
              textColor="text-black-500"
              backgroundColor="bg-functionalGreen"
              width={18}
              type="submit"
              height={3}
              isDisabled={!isValid || selectedField?.value?.isUnavailable}
              isLoading={isSubmitting}
              loadingText={t(COMMON_TRANSLATE_KEYS.BOOKING_IN_PROGRESS)}
            >
              {t(COMMON_TRANSLATE_KEYS.BOOK)}
            </Button>
          </div>
        </div>
      </form>

      <MyDrawer isOpen={isDateDrawerOpen}>
        <div className="bg-additionalBlack">
          <div className="flex justify-between my-4">
            <p>{t(COMMON_TRANSLATE_KEYS.SELECT_DATE)}</p>
            <button onClick={closeDrawer} className="text-white">
              X
            </button>
          </div>
          {/* We unmount the calendar in order to make use of the openToDate prop */}
          {isDateDrawerOpen && (
            <DateGridCalendar
              onSelectDate={handleDateSelect}
              selectedDate={selectedDate}
              filteredDates={filteredDates}
              onMonthDateChange={(month: number, year: number) =>
                fetchReservationSlots(undefined, month, year)
              }
              openToDate={selectedDate || new Date()}
            />
          )}
        </div>
      </MyDrawer>

      <MyDrawer isOpen={isRecurringDateDrawerOpen}>
        <div className="bg-additionalBlack">
          <div className="flex justify-between my-4">
            <p>{t(COMMON_TRANSLATE_KEYS.SELECT_DATE)}</p>
            <button onClick={closeRecurringDateDrawer} className="text-white">
              X
            </button>
          </div>
          {/* We unmount the calendar in order to make use of the openToDate prop */}
          {isRecurringDateDrawerOpen && (
            <DateGridCalendar
              onSelectDate={handleRecurringDateSelect}
              selectedDate={selectedRecurringDate}
              filteredDates={filteredDates}
              onMonthDateChange={(month: number, year: number) =>
                fetchReservationSlots(undefined, month, year)
              }
              openToDate={selectedRecurringDate || new Date()}
            />
          )}
        </div>
      </MyDrawer>

      <MyDrawer isOpen={isTimeDrawerOpen}>
        <div className="bg-additionalBlack">
          <div className="flex justify-between my-4">
            <p>{t(COMMON_TRANSLATE_KEYS.SELECT_TIME)}</p>
            <button onClick={closeDrawer} className="text-white">
              X
            </button>
          </div>
          <TimeGridCalendar
            timeSlots={timeSlots}
            onSelectTime={handleTimeSelect}
            isRecurring={isRecurring}
            initialTime={selectedTime}
          />
        </div>
      </MyDrawer>
    </div>
  );
};

export default BookField;
